import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import DocumentsCard from '@/components/super-admin/DocumentsCard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, 
  Settings, 
  DollarSign, 
  FileText, 
  Calendar, 
  ClipboardList,
  Car,
  TrendingUp,
  Send,
  Plus,
  Pencil,
  Trash2,
  Key,
  UserPlus,
  Shield,
  Building2,
  LayoutDashboard,
  Upload,
  Store,
  MapPin,
  Phone,
  Mail,
  Wrench,
  ToggleLeft,
  Clock,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-red-100 text-red-700' },
  { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-700' },
  { value: 'service_manager', label: 'Service Manager', color: 'bg-blue-100 text-blue-700' },
  { value: 'floor_manager', label: 'Floor Manager', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'mechanic', label: 'Mechanic / Technician', color: 'bg-green-100 text-green-700' },
  { value: 'parts_staff', label: 'Parts Staff', color: 'bg-amber-100 text-amber-700' }
];

const ROLE_ACCESS = {
  super_admin: ['All Features', 'User Management', 'Site Settings', 'Financials', 'Reports'],
  admin: ['All features (own shop)', 'Financials', 'Reports', 'Staff Management', 'Settings'],
  service_manager: ['Bookings', 'Job Cards', 'Customers', 'Invoices', 'Loan Cars', 'Comms'],
  floor_manager: ['Bookings', 'Job Cards', 'Customers'],
  mechanic: ['Job Cards (Assigned)', 'Update Work Details'],
  parts_staff: ['Parts Catalog', 'Inventory']
};

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // User Management State
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'mechanic',
    site_id: '',
    daily_hours: '',
    working_days_per_week: '',
    mobile: '',
    chat_visible: true,
  });
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  // iter138.9y — Filters for User Management. By default, only ACTIVE
  // users from the SA's currently-selected workshop are visible (per user
  // request: "by default only active members should be visible, inactive
  // or deleted should not be visible"). Both filters can be relaxed via
  // dropdowns above the table.
  const [userFilterActiveOnly, setUserFilterActiveOnly] = useState(true);
  const [userFilterSiteId, setUserFilterSiteId] = useState('');
  const [userFilterRole, setUserFilterRole] = useState('');

  // Workshop Management State
  const [workshops, setWorkshops] = useState([]);
  const [workshopsLoading, setWorkshopsLoading] = useState(false);
  const [showWorkshopDialog, setShowWorkshopDialog] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [workshopForm, setWorkshopForm] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    type: 'mechanical',
    status: 'active',
    subscription: 'essential'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) {
          navigate('/login');
          return;
        }
        const userData = await response.json();
        
        if (userData.role !== 'super_admin' && userData.role !== 'admin') {
          toast.error('Owner access required');
          navigate('/');
          return;
        }
        
        setUser(userData);
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      // iter138.9y — Pass filters to the backend so we don't ship the full
      // (potentially soft-deleted) user list to the SA's browser.
      const params = new URLSearchParams({
        active_only: userFilterActiveOnly ? 'true' : 'false',
        page_size: '200',
      });
      if (userFilterSiteId) params.set('site_id', userFilterSiteId);
      if (userFilterRole) params.set('role', userFilterRole);
      const response = await fetch(`${BACKEND_URL}/api/users?${params.toString()}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  // iter138.9y — Re-fetch when any filter changes.
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
     
  }, [userFilterActiveOnly, userFilterSiteId, userFilterRole]);

  useEffect(() => {
    if (activeTab === 'users' && users.length === 0) {
      fetchUsers();
    }
    // iter238 bug fix — the Users LIST shows each user's assigned workshop
    // via getSiteName(), so workshops must load with the users tab too
    // (previously they only loaded when the edit dialog opened).
    if ((activeTab === 'users' || activeTab === 'workshops') && workshops.length === 0) {
      fetchWorkshops();
    }
    // Also fetch workshops when opening user dialog for site selection
    if (showUserDialog && workshops.length === 0) {
      fetchWorkshops();
    }
  }, [activeTab, showUserDialog]);

  // Fetch workshops for site selection
  const fetchWorkshops = async () => {
    setWorkshopsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sites`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWorkshops(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch workshops:', error);
      toast.error('Failed to load workshops');
    } finally {
      setWorkshopsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const body = { ...userForm };
      // iter138.9y — Only send the numeric targets when filled in.
      body.daily_hours = body.daily_hours === '' ? null : Number(body.daily_hours);
      body.working_days_per_week = body.working_days_per_week === '' ? null : Number(body.working_days_per_week);
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success('User created successfully');
        setShowUserDialog(false);
        setUserForm({ name: '', email: '', password: '', role: 'mechanic', site_id: '', daily_hours: '', working_days_per_week: '', mobile: '', chat_visible: true });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${editingUser.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: userForm.name,
          // iter138.9s — allow admin to fix a user's login email so they
          // can log in with the new credentials without losing their
          // historical job cards (which are linked by user_id, not email).
          email: userForm.email ? userForm.email.trim().toLowerCase() : undefined,
          role: userForm.role,
          site_id: userForm.site_id || null,
          // iter138.9y — Per-mechanic target hours. Send null when blank
          // so backend falls back to legacy 7.6h × 5 days defaults.
          daily_hours: userForm.daily_hours === '' || userForm.daily_hours == null ? null : Number(userForm.daily_hours),
          working_days_per_week: userForm.working_days_per_week === '' || userForm.working_days_per_week == null ? null : Number(userForm.working_days_per_week),
          // iter138.9z — Mobile for SMS notifications (SA daily digest etc).
          mobile: (userForm.mobile || '').trim() || null,
          // iter138.9aa — Pulse chat visibility (SA can hide users).
          chat_visible: userForm.chat_visible !== false,
        })
      });
      
      if (response.ok) {
        toast.success('User updated successfully');
        setShowUserDialog(false);
        setEditingUser(null);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editingUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${editingUser.user_id}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ new_password: newPassword })
      });
      
      if (response.ok) {
        toast.success('Password reset successfully');
        setShowPasswordDialog(false);
        setNewPassword('');
        setEditingUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('User deleted successfully');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const openEditDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    setUserForm({
      name: userToEdit.name,
      email: userToEdit.email,
      password: '',
      role: userToEdit.role,
      site_id: userToEdit.site_id || '',
      // iter138.9y — Pre-fill target-hours fields if set on the user.
      daily_hours: userToEdit.daily_hours != null ? String(userToEdit.daily_hours) : '',
      working_days_per_week: userToEdit.working_days_per_week != null ? String(userToEdit.working_days_per_week) : '',
      mobile: userToEdit.mobile || '',
      // iter138.9aa — Default to true when undefined (legacy users).
      chat_visible: userToEdit.chat_visible !== false,
    });
    setShowUserDialog(true);
  };

  const openPasswordDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  // Workshop Management Functions
  const handleCreateWorkshop = async () => {
    if (!workshopForm.name || !workshopForm.address || !workshopForm.phone || !workshopForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(workshopForm)
      });
      
      if (response.ok) {
        toast.success('Workshop created successfully');
        setShowWorkshopDialog(false);
        setWorkshopForm({ name: '', address: '', phone: '', email: '', type: 'mechanical', status: 'active', subscription: 'essential' });
        fetchWorkshops();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create workshop');
      }
    } catch (error) {
      toast.error('Failed to create workshop');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWorkshop = async () => {
    if (!editingWorkshop) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sites/${editingWorkshop.site_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: workshopForm.name,
          address: workshopForm.address,
          phone: workshopForm.phone,
          email: workshopForm.email,
          status: workshopForm.status,
          subscription: workshopForm.subscription
        })
      });
      
      if (response.ok) {
        toast.success('Workshop updated successfully');
        if (editingWorkshop?.subscription !== workshopForm.subscription) {
          toast.info('Subscription changed — staff at this workshop will see the new plan after they reload or log in again.');
        }
        setShowWorkshopDialog(false);
        setEditingWorkshop(null);
        fetchWorkshops();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update workshop');
      }
    } catch (error) {
      toast.error('Failed to update workshop');
    } finally {
      setSaving(false);
    }
  };

  const openEditWorkshopDialog = (workshop) => {
    setEditingWorkshop(workshop);
    setWorkshopForm({
      name: workshop.name,
      address: workshop.address,
      phone: workshop.phone,
      email: workshop.email,
      type: workshop.type,
      status: workshop.status || 'active',
      subscription: workshop.subscription || 'essential'
    });
    setShowWorkshopDialog(true);
  };

  const getWorkshopName = (siteId) => {
    const workshop = workshops.find(w => w.site_id === siteId);
    return workshop?.name || 'Not Assigned';
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLE_OPTIONS.find(r => r.value === role);
    return (
      <Badge className={roleConfig?.color || 'bg-slate-100 text-slate-700'}>
        {roleConfig?.label || role}
      </Badge>
    );
  };

  // iter189 — a shop `admin` sees this hub WITHOUT platform tools (sites,
  // cross-tenant data import) and can only create/manage non-privileged staff.
  const isPlatformSuperAdmin = user?.role === 'super_admin';
  const formRoleOptions = isPlatformSuperAdmin
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((r) => !['super_admin', 'admin'].includes(r.value));

  // Dashboard Tiles
  const dashboardTiles = [
    { title: 'Workshops', description: 'Manage branches & sites', icon: Store, color: 'bg-violet-600', onClick: () => setActiveTab('workshops'), saOnly: true },
    { title: 'User Management', description: 'Add users and manage roles', icon: Users, color: 'bg-blue-500', onClick: () => setActiveTab('users') },
    { title: 'Data Import', description: 'Import customers & vehicles from Excel', icon: Upload, color: 'bg-indigo-600', onClick: () => navigate('/data-import'), saOnly: true },
    { title: 'Site Settings', description: 'Configure business details', icon: Settings, color: 'bg-slate-600', onClick: () => navigate('/settings') },
    { title: 'Financials', description: 'Income, expenses & P&L', icon: DollarSign, color: 'bg-green-500', onClick: () => navigate('/financials') },
    { title: 'Bookings', description: 'View all bookings', icon: Calendar, color: 'bg-orange-500', onClick: () => navigate('/bookings') },
    { title: 'Job Cards', description: 'Manage workshop jobs', icon: ClipboardList, color: 'bg-blue-600', onClick: () => navigate('/job-cards') },
    { title: 'Customers', description: 'Customer database', icon: Users, color: 'bg-emerald-500', onClick: () => navigate('/customers') },
    { title: 'Invoices', description: 'Create & manage invoices', icon: FileText, color: 'bg-amber-500', onClick: () => navigate('/invoices') },
    { title: 'Loan Cars', description: 'Track loan vehicles', icon: Car, color: 'bg-cyan-500', onClick: () => navigate('/loan-cars') },
    { title: 'Reports', description: 'Analytics & reports', icon: TrendingUp, color: 'bg-purple-500', onClick: () => navigate('/reports') },
    { title: 'Comms', description: 'Send SMS & Email', icon: Send, color: 'bg-pink-500', onClick: () => navigate('/comms') },
    { title: 'Templates', description: 'Manage checklists, SMS, email, invoice items & promos', icon: ClipboardList, color: 'bg-violet-600', onClick: () => navigate('/super-admin/templates') },
    { title: 'Service Mapping', description: 'Auto-populate JC tasks + pricing when SM/kiosk picks a service', icon: Wrench, color: 'bg-teal-600', onClick: () => navigate('/super-admin/service-mappings') },
    { title: 'Documentation', description: 'PDFs, codebase, & in-app Confluence wiki', icon: BookOpen, color: 'bg-gradient-to-br from-indigo-600 to-blue-700', onClick: () => navigate('/documentation') },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="super-admin-dashboard">
        <div className="mb-8">
          <h1 className="font-bold text-3xl text-slate-900 mb-2">
            Super Admin Dashboard
          </h1>
          <p className="text-slate-500">
            Manage users, settings, and access all system features
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 mb-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            {isPlatformSuperAdmin && (
              <TabsTrigger value="workshops" className="flex items-center gap-2">
                <Store className="h-4 w-4" />
                Workshops
              </TabsTrigger>
            )}
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2" data-testid="tab-roles">
              <ToggleLeft className="h-4 w-4" />
              Roles
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab - Feature Tiles */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {dashboardTiles.filter((t) => isPlatformSuperAdmin || !t.saOnly).map((tile, index) => (
                <Card 
                  key={index}
                  className="p-4 cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-2 border-transparent hover:border-blue-200"
                  onClick={tile.onClick}
                  data-testid={`tile-${tile.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`w-12 h-12 ${tile.color} rounded-lg flex items-center justify-center mb-3`}>
                    <tile.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{tile.title}</h3>
                  <p className="text-sm text-slate-500">{tile.description}</p>
                </Card>
              ))}
            </div>

            {/* Role Access Reference */}
            <Card className="mt-8 p-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                Role Access Reference
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ROLE_OPTIONS.map((role) => (
                  <div key={role.value} className="p-4 bg-slate-50 rounded-lg">
                    <Badge className={role.color + ' mb-2'}>{role.label}</Badge>
                    <ul className="text-sm text-slate-600 space-y-1">
                      {ROLE_ACCESS[role.value]?.map((access, i) => (
                        <li key={i}>• {access}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Card>

            {/* Platform Documents (PDF download) */}
            <DocumentsCard />

            {/* Data Maintenance — DB-wide fixers triggered on demand. */}
            <Card className="mt-8 p-6" data-testid="data-maintenance-card">
              <h2 className="font-semibold text-lg mb-2 flex items-center gap-2">
                <Wrench className="h-5 w-5 text-amber-600" />
                Data Maintenance
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                One-click DB-wide fixers. Safe to re-run — each operation is idempotent.
              </p>
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">Normalise all phone numbers</div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Reformats every <strong>mobile</strong> + <strong>phone</strong> field across customers, leads,
                      bookings, job cards, invoices, loan cars & quotes to the unified <code className="bg-white px-1 rounded">0415 837 157</code> format.
                      Run this any time you spot a `+61...` or `61...` entry slipping through.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm('Normalise every phone number in the database to "0415 837 157" format? Safe to re-run — but recommended to do once after large imports.')) return;
                      const toastId = toast.loading('Normalising phones across the database…');
                      try {
                        const r = await fetch(
                          `${BACKEND_URL}/api/super-admin/normalise-phones`,
                          { method: 'POST', credentials: 'include' },
                        );
                        if (!r.ok) {
                          const e = await r.json().catch(() => ({}));
                          toast.error(e.detail || 'Failed to normalise phones', { id: toastId });
                          return;
                        }
                        const d = await r.json();
                        const summary = d.per_collection
                          .filter(c => c.updates > 0)
                          .map(c => `${c.collection}: ${c.updates}`)
                          .join(', ') || 'all already in canonical format';
                        toast.success(`✓ ${d.total_updates} phones normalised (${summary})`, { id: toastId, duration: 8000 });
                      } catch (_e) {
                        toast.error('Network error', { id: toastId });
                      }
                    }}
                    className="flex-shrink-0 border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="normalise-phones-btn"
                  >
                    Normalise now
                  </Button>
                </div>
                <div className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">Repair Job Card vehicle / customer drift</div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Fixes the bug where the JC kanban tile shows the <strong>correct</strong> rego/make/model
                      but the JC modal's "Edit vehicle details" dialog (or the generated invoice) shows a
                      <strong> different</strong> car. Sweeps every JC and either re-links the
                      JC to a matching existing vehicle on the same customer, or rewrites the linked vehicle
                      doc to match the JC snapshot. Run <code className="bg-white px-1 rounded">Dry-run</code> first to preview.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        const toastId = toast.loading('Scanning all JCs (dry-run, no writes)…');
                        try {
                          const r = await fetch(
                            `${BACKEND_URL}/api/super-admin/repair-jc-drift?dry_run=true`,
                            { method: 'POST', credentials: 'include' },
                          );
                          if (!r.ok) {
                            const e = await r.json().catch(() => ({}));
                            toast.error(e.detail || 'Failed to scan', { id: toastId });
                            return;
                          }
                          const d = await r.json();
                          const c = d.counts || {};
                          const summary = [
                            `Scanned ${d.scanned}`,
                            `re-link ${c.vehicle_relinked || 0}`,
                            `snapshot→vehicle ${c.vehicle_updated_from_snapshot || 0}`,
                            `orphan vehicles ${c.vehicle_orphans || 0}`,
                            `orphan customers ${c.customer_orphans || 0}`,
                            `name drift ${c.customer_name_drift || 0}`,
                            `cust-id collisions ${c.customer_id_collisions || 0}`,
                          ].join(' · ');
                          toast.success(`Dry-run done: ${summary}`, { id: toastId, duration: 12000 });
                           
                          console.log('[REPAIR-JC-DRIFT dry-run]', d);
                        } catch (_e) {
                          toast.error('Network error', { id: toastId });
                        }
                      }}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid="repair-jc-drift-dryrun-btn"
                    >
                      Dry-run
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        if (!window.confirm('Apply JC drift repairs to the database? Run "Dry-run" first if you have not — preview the changes in the browser console. Safe to re-run.')) return;
                        const toastId = toast.loading('Repairing JCs…');
                        try {
                          const r = await fetch(
                            `${BACKEND_URL}/api/super-admin/repair-jc-drift?dry_run=false`,
                            { method: 'POST', credentials: 'include' },
                          );
                          if (!r.ok) {
                            const e = await r.json().catch(() => ({}));
                            toast.error(e.detail || 'Failed to repair', { id: toastId });
                            return;
                          }
                          const d = await r.json();
                          const c = d.counts || {};
                          toast.success(
                            `✓ Applied — re-linked ${c.vehicle_relinked || 0} JCs, updated ${c.vehicle_updated_from_snapshot || 0} vehicle docs from JC snapshot. ${c.vehicle_orphans || 0} orphan vehicles and ${c.customer_orphans || 0} orphan customers need manual review.`,
                            { id: toastId, duration: 12000 },
                          );
                           
                          console.log('[REPAIR-JC-DRIFT applied]', d);
                        } catch (_e) {
                          toast.error('Network error', { id: toastId });
                        }
                      }}
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid="repair-jc-drift-apply-btn"
                    >
                      Apply repairs
                    </Button>
                  </div>
                </div>
                <div className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">Backfill wage history baseline</div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      One-click healer for the bug where editing a mechanic&apos;s <strong>weekly wage</strong> retroactively
                      corrupted past months&apos; P&L / cash-flow / fixed-expense burden. Seeds an
                      <code className="bg-white px-1 rounded">effective_from = 1970-01-01</code> baseline on every legacy
                      payroll record so historical periods stay anchored at the OLD wage. Idempotent — already-healed
                      records are skipped.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm('Seed wage_history baseline (effective_from=1970-01-01) on every legacy payroll doc? This preserves historical wage values so future edits no longer corrupt past months. Safe to re-run — already-healed records are skipped.')) return;
                      const toastId = toast.loading('Backfilling wage history baselines…');
                      try {
                        const r = await fetch(
                          `${BACKEND_URL}/api/super-admin/cash-incentives/payroll/backfill-baseline`,
                          { method: 'POST', credentials: 'include' },
                        );
                        if (!r.ok) {
                          const e = await r.json().catch(() => ({}));
                          toast.error(e.detail || 'Failed to backfill', { id: toastId });
                          return;
                        }
                        const d = await r.json();
                        toast.success(
                          `✓ Healed ${d.healed} of ${d.total} payroll records · ${d.untouched} already had history (skipped). Past months now anchored at OLD wage.`,
                          { id: toastId, duration: 10000 },
                        );
                      } catch (_e) {
                        toast.error('Network error', { id: toastId });
                      }
                    }}
                    className="flex-shrink-0 border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="backfill-wage-baseline-btn"
                  >
                    Backfill now
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Workshops Management Tab */}
          <TabsContent value="workshops">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Store className="h-5 w-5 text-blue-600" />
                    Workshop Management
                  </h2>
                  <p className="text-sm text-slate-500">Manage your workshops/branches and their details</p>
                </div>
                <Button 
                  onClick={() => {
                    setEditingWorkshop(null);
                    setWorkshopForm({ name: '', address: '', phone: '', email: '', type: 'mechanical', status: 'active', subscription: 'essential' });
                    setShowWorkshopDialog(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="add-workshop-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Workshop
                </Button>
              </div>

              {workshopsLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {workshops.map((workshop) => (
                    <Card key={workshop.site_id} className="p-4 border-2 hover:border-blue-200 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            {workshop.type === 'mechanical' ? (
                              <Wrench className="h-5 w-5 text-blue-600" />
                            ) : (
                              <Car className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{workshop.name}</h3>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge className={workshop.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                {workshop.status === 'active' ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge
                                className={
                                  workshop.subscription === 'pro' ? 'bg-violet-100 text-violet-700'
                                    : workshop.subscription === 'standard' ? 'bg-sky-100 text-sky-700'
                                      : 'bg-slate-100 text-slate-600'
                                }
                                data-testid={`workshop-subscription-${workshop.site_id}`}
                              >
                                {(workshop.subscription || 'essential').toUpperCase()}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditWorkshopDialog(workshop)}
                          title="Edit Workshop"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-2 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <span className="truncate">{workshop.address}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-slate-400" />
                          <span>{workshop.phone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <span className="truncate">{workshop.email}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>ID: {workshop.site_id}</span>
                          <Badge variant="outline" className="text-xs">
                            {workshop.type === 'mechanical' ? 'Mechanical' : 'Smash Repair'}
                          </Badge>
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  {workshops.length === 0 && (
                    <div className="col-span-full text-center py-12 text-slate-500">
                      <Store className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No workshops found. Add your first workshop to get started.</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    User Management
                  </h2>
                  <p className="text-sm text-slate-500">Create users, assign roles, and manage access</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* iter138.9ak — Backfill historical task credit.
                      Stamps `completed_by` on legacy completed tasks
                      via smart time_log attribution. Run once after
                      redeploy to fix per-mechanic productivity numbers
                      for old JCs. Idempotent. */}
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const r = await fetch(`${BACKEND_URL}/api/admin/backfill-line-item-completed-by`, {
                          method: 'POST', credentials: 'include',
                        });
                        if (!r.ok) throw new Error((await r.json()).detail || 'Backfill failed');
                        const data = await r.json();
                        toast.success(data.message || `Stamped ${data.stamped_items} task(s).`,
                          { duration: 12000 });
                      } catch (e) { toast.error(e.message || 'Backfill failed'); }
                    }}
                    data-testid="backfill-completed-by-btn"
                    title="One-click: backfill historical task attribution so each mechanic's allocated/produced hours show up correctly. Uses time_log session timing — most accurate for reassigned JCs."
                  >
                    🔄 Backfill task credit
                  </Button>
                  <Button 
                    onClick={() => {
                      setEditingUser(null);
                      setUserForm({ name: '', email: '', password: '', role: 'mechanic', site_id: '', daily_hours: '', working_days_per_week: '', mobile: '', chat_visible: true });
                      setShowUserDialog(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="add-user-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </div>
              </div>

              {/* iter138.9y — Filters: active-only (default on), per-site,
                  and role. Users requested this so the SA can drill into
                  one workshop's active mechanics without scrolling past
                  retired / leaving staff. */}
              <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-slate-50 rounded-md border border-slate-200" data-testid="user-filters-row">
                <span className="text-xs uppercase tracking-wider text-slate-600 font-bold">Filter:</span>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userFilterActiveOnly}
                    onChange={(e) => setUserFilterActiveOnly(e.target.checked)}
                    className="rounded"
                    data-testid="user-filter-active-only"
                  />
                  <span>Active only</span>
                </label>
                {isPlatformSuperAdmin && (
                <Select
                  value={userFilterSiteId || '__all__'}
                  onValueChange={(v) => setUserFilterSiteId(v === '__all__' ? '' : v)}
                >
                  <SelectTrigger className="h-8 w-[200px]" data-testid="user-filter-site">
                    <SelectValue placeholder="All workshops" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All workshops</SelectItem>
                    {workshops.map((w) => (
                      <SelectItem key={w.site_id} value={w.site_id}>
                        <div className="flex items-center gap-2">
                          <Store className="h-3 w-3 text-slate-400" />
                          {w.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                )}
                <Select
                  value={userFilterRole || '__all_roles__'}
                  onValueChange={(v) => setUserFilterRole(v === '__all_roles__' ? '' : v)}
                >
                  <SelectTrigger className="h-8 w-[160px]" data-testid="user-filter-role">
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all_roles__">All roles</SelectItem>
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="ml-auto text-xs text-slate-500" data-testid="user-filter-count">
                  {users.length} user{users.length === 1 ? '' : 's'}
                </span>
              </div>

              {usersLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Workshop</TableHead>
                      <TableHead>Target hrs</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{getRoleBadge(u.role)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Store className="h-3 w-3 text-slate-400" />
                            <span className="text-sm">{getWorkshopName(u.site_id)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {/* iter138.9y — surface per-user schedule. */}
                          {u.daily_hours != null || u.working_days_per_week != null ? (
                            <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`user-target-hrs-${u.user_id}`}>
                              <Clock className="h-3 w-3" />
                              {u.daily_hours != null ? `${u.daily_hours}h` : '7.6h'}
                              <span className="text-slate-400">·</span>
                              {u.working_days_per_week != null ? `${u.working_days_per_week}d` : '5d'}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">7.6h · 5d (default)</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={u.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {u.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(u)}
                              title="Edit User"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openPasswordDialog(u)}
                              title="Reset Password"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(u.user_id, u.name)}
                              className="text-red-500 hover:text-red-700"
                              title="Delete User"
                              disabled={u.user_id === user?.user_id}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          {/* Roles / Feature Toggles Tab */}
          <TabsContent value="roles">
            <RolesFeatureToggleCard
              currentUserId={user?.user_id}
              workshops={workshops}
            />
          </TabsContent>
        </Tabs>

        {/* Create/Edit User Dialog */}
        <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
              <DialogDescription>
                {editingUser ? 'Update user details and role' : 'Add a new user to the system'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  placeholder="e.g., John Smith"
                  className="mt-1"
                />
              </div>
              
              {!editingUser && (
                <>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      placeholder="e.g., john@workshop.com"
                      className="mt-1"
                      data-testid="user-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      placeholder="Minimum 6 characters"
                      className="mt-1"
                      data-testid="user-password-input"
                    />
                  </div>
                </>
              )}

              {/* iter138.9s — admin can change a user's login email AFTER
                  creation too (e.g. mechanic who never set up their own
                  credentials and the SM is setting them up now). The
                  password lives in a separate dialog (Key icon) to keep
                  this form focused. */}
              {editingUser && (
                <div>
                  <Label htmlFor="edit-email">Email (login username)</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    placeholder="e.g., john@workshop.com"
                    className="mt-1"
                    data-testid="user-edit-email-input"
                  />
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <p className="text-slate-500">
                      Their historical job cards stay linked — only the login
                      email changes. They'll use the new email next time they log in.
                    </p>
                    <button
                      type="button"
                      className="text-blue-600 hover:underline whitespace-nowrap ml-3 inline-flex items-center gap-1"
                      onClick={() => {
                        // Hand off to the existing Reset Password dialog.
                        setShowUserDialog(false);
                        openPasswordDialog(editingUser);
                      }}
                      data-testid="user-edit-change-password-btn"
                    >
                      <Key className="h-3 w-3" />
                      Change password
                    </button>
                  </div>
                </div>
              )}
              
              <div>
                <Label htmlFor="role">Role *</Label>
                <Select 
                  value={userForm.role} 
                  onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formRoleOptions.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Access: {ROLE_ACCESS[userForm.role]?.join(', ')}
                </p>
              </div>

              <div>
                <Label htmlFor="site">Workshop *</Label>
                {isPlatformSuperAdmin ? (
                  <Select 
                    value={userForm.site_id || ''} 
                    onValueChange={(value) => setUserForm({ ...userForm, site_id: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="user-workshop-select">
                      <SelectValue placeholder="Select a workshop" />
                    </SelectTrigger>
                    <SelectContent>
                      {workshops.map((workshop) => (
                        <SelectItem key={workshop.site_id} value={workshop.site_id}>
                          <div className="flex items-center gap-2">
                            <Store className="h-4 w-4 text-slate-400" />
                            {workshop.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div
                    className="mt-1 flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700"
                    data-testid="user-workshop-fixed"
                  >
                    <Store className="h-4 w-4 text-slate-400" />
                    {workshops.find(w => w.site_id === user?.site_id)?.name || workshops[0]?.name || 'Your workshop'}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  {isPlatformSuperAdmin
                    ? 'User will only see data from this workshop'
                    : 'New staff are added to your workshop automatically'}
                </p>
              </div>

              {/* iter138.9z — Mobile (for SA daily-digest SMS etc). Shown
                  for every role since any user may need SMS notifications
                  in future workflows. */}
              <div>
                <Label htmlFor="mobile">Mobile (for SMS notifications)</Label>
                <Input
                  id="mobile"
                  type="tel"
                  value={userForm.mobile}
                  onChange={(e) => setUserForm({ ...userForm, mobile: e.target.value })}
                  placeholder="e.g. 04XX XXX XXX"
                  className="mt-1"
                  data-testid="user-mobile-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Super Admins receive a daily 6 PM digest with each mechanic's hours produced + tasks completed today.
                </p>
              </div>

              {/* iter138.9aa — Pulse Chat visibility toggle. SA-controlled.
                  When unchecked, this user is hidden from every other
                  user's Workshop Pulse contacts list. Useful for retired
                  staff or parts-counter logins. */}
              <div className="p-3 bg-violet-50 border border-violet-200 rounded-md">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userForm.chat_visible !== false}
                    onChange={(e) => setUserForm({ ...userForm, chat_visible: e.target.checked })}
                    className="mt-0.5"
                    data-testid="user-chat-visible-toggle"
                  />
                  <div>
                    <p className="text-sm font-semibold text-violet-900">Visible in Workshop Pulse chat</p>
                    <p className="text-[11px] text-violet-700">
                      When OFF, this user is hidden from everyone's chat contacts list. Existing messages stay; nobody can start a new DM with them.
                    </p>
                  </div>
                </label>
              </div>

              {/* iter138.9y — Target hours config: applied to mechanic
                  productivity calculations across the whole system. SA
                  can set per-mechanic schedules (e.g. part-timers). Blank
                  fields fall back to 7.6h/day × 5 days/week. */}
              {(userForm.role === 'mechanic' || userForm.role === 'floor_manager') && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <div>
                    <Label htmlFor="daily-hours" className="text-xs">Hours per day</Label>
                    <Input
                      id="daily-hours"
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      value={userForm.daily_hours}
                      onChange={(e) => setUserForm({ ...userForm, daily_hours: e.target.value })}
                      placeholder="e.g. 8 or 7.6"
                      className="mt-1"
                      data-testid="user-daily-hours-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="working-days" className="text-xs">Working days / week</Label>
                    <Input
                      id="working-days"
                      type="number"
                      step="1"
                      min="0"
                      max="7"
                      value={userForm.working_days_per_week}
                      onChange={(e) => setUserForm({ ...userForm, working_days_per_week: e.target.value })}
                      placeholder="e.g. 5"
                      className="mt-1"
                      data-testid="user-working-days-input"
                    />
                  </div>
                  <p className="col-span-2 text-[11px] text-amber-800 leading-tight">
                    Used as target hours for productivity calculations everywhere. Blank = 7.6h × 5 days default.
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUserDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={editingUser ? handleUpdateUser : handleCreateUser}
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Set a new password for {editingUser?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="mt-1"
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleResetPassword} disabled={saving}>
                {saving ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Workshop Dialog */}
        <Dialog open={showWorkshopDialog} onOpenChange={setShowWorkshopDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingWorkshop ? 'Edit Workshop' : 'Create New Workshop'}</DialogTitle>
              <DialogDescription>
                {editingWorkshop ? 'Update workshop details' : 'Add a new workshop/branch to your business'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="workshop_name">Workshop Name *</Label>
                <Input
                  id="workshop_name"
                  value={workshopForm.name}
                  onChange={(e) => setWorkshopForm({ ...workshopForm, name: e.target.value })}
                  placeholder="e.g., Hoppers Crossing Workshop"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="workshop_address">Address *</Label>
                <Textarea
                  id="workshop_address"
                  value={workshopForm.address}
                  onChange={(e) => setWorkshopForm({ ...workshopForm, address: e.target.value })}
                  placeholder="Full address including suburb and postcode"
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="workshop_phone">Phone *</Label>
                  <Input
                    id="workshop_phone"
                    value={workshopForm.phone}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, phone: e.target.value })}
                    placeholder="e.g., 03 9749 0244"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="workshop_email">Email *</Label>
                  <Input
                    id="workshop_email"
                    type="email"
                    value={workshopForm.email}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, email: e.target.value })}
                    placeholder="e.g., info@workshop.com"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="workshop_type">Workshop Type *</Label>
                  <Select 
                    value={workshopForm.type} 
                    onValueChange={(value) => setWorkshopForm({ ...workshopForm, type: value })}
                    disabled={editingWorkshop} // Can't change type after creation
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mechanical">
                        <div className="flex items-center gap-2">
                          <Wrench className="h-4 w-4" />
                          Mechanical
                        </div>
                      </SelectItem>
                      <SelectItem value="smash_repair">
                        <div className="flex items-center gap-2">
                          <Car className="h-4 w-4" />
                          Smash Repair
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="workshop_status">Status</Label>
                  <Select 
                    value={workshopForm.status} 
                    onValueChange={(value) => setWorkshopForm({ ...workshopForm, status: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="workshop_subscription">Subscription Plan</Label>
                <Select
                  value={workshopForm.subscription}
                  onValueChange={(value) => setWorkshopForm({ ...workshopForm, subscription: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="workshop-subscription-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="essential" data-testid="subscription-option-essential">Essential — core workshop tools</SelectItem>
                    <SelectItem value="standard" data-testid="subscription-option-standard">Standard — + Comms, Loan Cars, Financials, Pulse Chat</SelectItem>
                    <SelectItem value="pro" data-testid="subscription-option-pro">Pro — everything incl. Memberships, AR, Marketing, HR</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400 mt-1">
                  Controls which modules this workshop's staff can access. Super Admins always see everything.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWorkshopDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={editingWorkshop ? handleUpdateWorkshop : handleCreateWorkshop}
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingWorkshop ? 'Update Workshop' : 'Create Workshop')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Roles / Feature Toggles
// ---------------------------------------------------------------------------
function RolesFeatureToggleCard({ workshops }) {
  const [catalog, setCatalog] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [draft, setDraft] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [catRes, usersRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/feature-flags/catalog`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/feature-flags`, { credentials: 'include' }),
      ]);
      if (catRes.ok) {
        const c = await catRes.json();
        setCatalog(c.features || []);
      }
      if (usersRes.ok) {
        const u = await usersRes.json();
        setUsersList(u.users || []);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const selectedUser = usersList.find(u => u.user_id === selectedUserId);
  const openUser = (u) => {
    setSelectedUserId(u.user_id);
    setDraft(new Set(u.disabled || []));
  };
  const toggleKey = (key) => {
    setDraft(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/feature-flags/${selectedUser.user_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ disabled: Array.from(draft) }),
      });
      if (res.ok) {
        toast.success(`Feature toggles saved for ${selectedUser.name}`);
        await loadAll();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } finally { setSaving(false); }
  };

  const workshopName = (siteId) => workshops.find(w => w.site_id === siteId)?.name || siteId || '—';
  const groupedCatalog = catalog.reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});
  const relevantCatalog = selectedUser
    ? catalog.filter(f => !f.roles || f.roles.includes(selectedUser.role))
    : [];

  return (
    <Card className="p-6">
      <div className="mb-6 flex items-start gap-3">
        <ToggleLeft className="h-5 w-5 text-purple-600 mt-1" />
        <div>
          <h2 className="text-lg font-semibold">Roles & Feature Toggles</h2>
          <p className="text-sm text-slate-500 max-w-2xl">
            Hide specific features from individual admin / manager accounts. Super Admins always see everything. Toggles take effect on the user's next page load.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: user list */}
          <div className="lg:col-span-5 border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b">
              Manageable Users ({usersList.length})
            </div>
            <div className="max-h-[520px] overflow-y-auto divide-y divide-slate-100" data-testid="roles-user-list">
              {usersList.length === 0 ? (
                <p className="text-sm text-slate-400 p-4 text-center">No admin / manager users found.</p>
              ) : usersList.map(u => (
                <button
                  key={u.user_id}
                  onClick={() => openUser(u)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center justify-between gap-3 ${selectedUserId === u.user_id ? 'bg-purple-50' : ''}`}
                  data-testid={`roles-user-${u.user_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{u.name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      <span className="capitalize">{u.role.replace('_', ' ')}</span> · {workshopName(u.site_id)}
                    </p>
                  </div>
                  {u.disabled.length > 0 && (
                    <Badge className="bg-rose-100 text-rose-700 text-[10px]">{u.disabled.length} off</Badge>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Right: feature toggles */}
          <div className="lg:col-span-7">
            {!selectedUser ? (
              <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-400 text-sm">
                Select a user on the left to manage their feature access.
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg">
                <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{selectedUser.name}</p>
                    <p className="text-xs text-slate-500"><span className="capitalize">{selectedUser.role.replace('_', ' ')}</span> · {selectedUser.email}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={save}
                    disabled={saving}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="roles-save"
                  >
                    {saving ? 'Saving…' : 'Save Toggles'}
                  </Button>
                </div>
                <div className="p-4 space-y-5 max-h-[520px] overflow-y-auto">
                  {Object.entries(groupedCatalog).map(([group, features]) => {
                    const visible = features.filter(f => relevantCatalog.includes(f));
                    if (visible.length === 0) return null;
                    return (
                      <div key={group}>
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{group}</p>
                        <div className="space-y-2">
                          {visible.map(f => {
                            const off = draft.has(f.key);
                            return (
                              <label
                                key={f.key}
                                className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${off ? 'border-rose-200 bg-rose-50/50' : 'border-slate-200 hover:bg-slate-50'}`}
                                data-testid={`roles-toggle-${f.key}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={off}
                                  onChange={() => toggleKey(f.key)}
                                  className="mt-1 h-4 w-4 accent-rose-600"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-medium text-slate-900">{f.label}</span>
                                    {off && <Badge className="bg-rose-600 text-white text-[10px]">HIDDEN</Badge>}
                                  </div>
                                  <p className="text-xs text-slate-500 mt-0.5">{f.description}</p>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {relevantCatalog.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-6">No toggles apply to this role.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
